import { useCallback } from 'react'
import { usePaginatedFetch } from 'hooks/use-fetch'
import { useDispatch, useSelector } from 'react-redux'
import {
  performPlayerSearch,
  resetResults,
  setSorting as setResultsSorting,
} from 'actions/search-actions'
import { AppState } from 'redux/reducers'
import {
  Paging,
  PublicPersonFilterOptions,
  Sorting,
  SortingDirection,
  SortingField,
} from 'types/global'

interface FetchOptions {
  filterOptions?: PublicPersonFilterOptions
  pageSize?: number
}

const usePlayerSearchWithSorting = (searchId: string, options: FetchOptions = {}) => {
  const dispatch = useDispatch()
  const { filterOptions, pageSize } = options
  const loadedPlayers = useSelector((state: AppState) => state.searchbar.results[searchId])
  const totalPlayers = useSelector((state: AppState) => state.searchbar.total[searchId])
  const isSearchPending = useSelector((state: AppState) => state.searchbar.resultsPending[searchId])
  const searchQuery = useSelector((state: AppState) => state.searchbar.searchQuery[searchId])
  const filters = useSelector((state: AppState) => state.searchbar.filters[searchId])
  const sorting = useSelector((state: AppState) => state.searchbar.sorting[searchId])
  const searchPlayers = useCallback(
    (paging: Paging) =>
      dispatch(
        performPlayerSearch(
          { searchQuery, filters: { ...filters, ...(filterOptions ?? {}) } },
          paging,
          searchId
        )
      ),
    [dispatch, searchQuery, filters, searchId, filterOptions]
  )
  const resetSearchResults = useCallback(
    () => dispatch(resetResults(searchId)),
    [dispatch, searchId]
  )
  const setSorting = useCallback(
    (sorting: Sorting) => {
      dispatch(setResultsSorting(sorting, searchId))
    },
    [dispatch, searchId]
  )

  const handleTableSorting = useCallback(
    (field: SortingField) => {
      if (field === sorting?.field) {
        setSorting({
          direction:
            sorting.direction === SortingDirection.ASC
              ? SortingDirection.DESC
              : SortingDirection.ASC,
          field: sorting.field,
        })
      } else {
        setSorting({ direction: SortingDirection.ASC, field })
      }
      resetSearchResults()
      searchPlayers({ limit: pageSize, skip: 0 })
    },
    [setSorting, resetSearchResults, searchPlayers, sorting, pageSize]
  )

  const [loadMorePlayers, isLoadingMorePlayers, allPlayersLoaded] = usePaginatedFetch(
    {
      fetch: searchPlayers,
      total: totalPlayers,
      clearResults: resetSearchResults,
      invalidateAutoFetch: typeof searchQuery !== 'string',
      pageSize,
    },
    [searchQuery, filters]
  )

  return {
    loadedPlayers,
    totalPlayers,
    isSearchPending,
    searchQuery,
    filters,
    sorting,
    loadMorePlayers,
    allPlayersLoaded,
    isLoadingMorePlayers,
    searchPlayers,
    resetSearchResults,
    setSorting,
    handleTableSorting,
  }
}

export default usePlayerSearchWithSorting
